import { supabase } from './supabase'
import { newIdempotencyKey } from './documents'
import { preparePage } from './capture'
import type { Payable } from './payments'

/**
 * Proof that something was paid.
 *
 * spec.md §13: "Proof attachment is offered, never required." That is the
 * whole design. A screenshot of the bank confirmation is worth a great deal
 * the day a supplier claims they were never paid, and worth nothing at all on
 * the other three hundred days — so it must be one tap when you happen to have
 * it, and completely silent when you do not.
 *
 * A proof is an ordinary document, linked to the obligation with the `proof`
 * relation from 0001. It deliberately does NOT go through the extraction
 * pipeline: a bank screenshot is not an invoice, and running it through an
 * invoice model would produce a confident reading of numbers that mean
 * something else entirely.
 */

export type ProofKind = 'photo' | 'reference' | 'note'

/**
 * A photo or screenshot of the payment.
 *
 * `state: 'processed'` from the start, which is what keeps it out of the job
 * queue. Rule 17 says never extract inside the upload; here there is nothing
 * to extract at all.
 */
export async function attachProofImage(
  payable: Payable,
  file: File,
  userId: string,
): Promise<string> {
  const page = await preparePage(file)

  const { data: doc, error } = await supabase
    .from('documents')
    .insert({
      space_id: payable.spaceId,
      idempotency_key: newIdempotencyKey(),
      uploaded_by: userId,
      source: 'manual',
      kind: 'proof',
      state: 'processed',
      page_count: 1,
    })
    .select('id')
    .single()
  if (error) throw error

  const documentId = doc.id as string
  const key = `${payable.spaceId}/${documentId}/1.jpg`

  const { error: upError } = await supabase.storage
    .from('documents')
    .upload(key, page.blob, { contentType: 'image/jpeg', upsert: true })
  if (upError) throw upError

  const { error: attError } = await supabase.from('attachments').insert({
    space_id: payable.spaceId,
    document_id: documentId,
    storage_key: key,
    content_type: 'image/jpeg',
    byte_size: page.blob.size,
    page_number: 1,
  })
  if (attError) throw attError

  await linkProof(payable, documentId, userId, 'photo')
  return documentId
}

/**
 * Proof that is only words: a bank reference, or a note about who confirmed it
 * on the phone. No file, so no document — it lives on the payment event, which
 * is where the rest of the payment's history already is.
 */
export async function attachProofNote(
  payable: Payable,
  text: string,
  userId: string,
  kind: Exclude<ProofKind, 'photo'> = 'note',
): Promise<void> {
  const trimmed = text.trim()
  if (!trimmed) throw new Error('Niets om te bewaren.')

  await supabase.from('activity_log').insert({
    space_id: payable.spaceId,
    actor_id: userId,
    action: 'payment.proof_added',
    entity_type: 'instalment',
    entity_id: payable.instalmentId,
    detail: { kind, text: trimmed },
  })
}

async function linkProof(
  payable: Payable,
  documentId: string,
  userId: string,
  kind: ProofKind,
): Promise<void> {
  const { error } = await supabase.from('document_links').insert({
    space_id: payable.spaceId,
    document_id: documentId,
    obligation_id: payable.obligationId,
    relation: 'proof',
  })
  // The same proof attached twice is harmless.
  if (error && error.code !== '23505') throw error

  await supabase.from('activity_log').insert({
    space_id: payable.spaceId,
    actor_id: userId,
    action: 'payment.proof_added',
    entity_type: 'instalment',
    entity_id: payable.instalmentId,
    detail: { kind, document_id: documentId },
  })
}

export interface Proof {
  documentId: string | null
  kind: ProofKind
  text: string | null
  at: string
}

/**
 * Everything attached to this payment as proof, oldest first.
 *
 * Read from the activity log rather than a table of its own, because that is
 * already where "who did what to this instalment" lives, and a proof is a
 * thing that happened rather than a property of the payment.
 */
export async function loadProof(payable: Payable): Promise<Proof[]> {
  const { data, error } = await supabase
    .from('activity_log')
    .select('detail, created_at')
    .eq('entity_type', 'instalment')
    .eq('entity_id', payable.instalmentId)
    .eq('action', 'payment.proof_added')
    .order('created_at')
  if (error) throw error

  return (data ?? []).map((row) => {
    const detail = (row.detail ?? {}) as Record<string, unknown>
    return {
      documentId: (detail.document_id as string) ?? null,
      kind: (detail.kind as ProofKind) ?? 'note',
      text: (detail.text as string) ?? null,
      at: row.created_at as string,
    }
  })
}
